import { zodResolver } from "@hookform/resolvers/zod";
import {
  CopyIcon,
  EyeIcon,
  EyeOffIcon,
  PlusIcon,
  RefreshCwIcon,
  Trash2Icon,
} from "lucide-react";
import { useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateHeading,
} from "@/components/ui/empty-state";
import {
  Description,
  FieldError,
  Input,
  Label,
  TextField,
} from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { TableRAC } from "@/components/ui/table-rac";
import { Text } from "@/components/ui/text";
import { toast } from "@/components/ui/toast";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { mutationResponse } from "@/utils/trpc";
import type { InstrumentDetail } from "../../types";
import { TabLayout } from "../TabLayout";

const createSystemUserSchema = z.object({
  username: z.string().min(1, "Username is required"),
});

type CreateSystemUserFormValues = z.infer<typeof createSystemUserSchema>;

export function InstrumentSystemUserForm({
  instrument,
}: {
  instrument: InstrumentDetail;
}) {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [revealedKeys, setRevealedKeys] = useState<Set<number>>(new Set());

  const toggleKeyVisibility = (userId: number) => {
    setRevealedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  // Query to get existing system users
  const {
    data: systemUsersData,
    isPending: isLoadingSystemUsers,
    refetch: refetchSystemUsers,
  } = trpc.admin.instrument.getSystemUsers.useQuery({
    instrumentId: instrument.id,
  });

  // Mutations
  const createSystemUser = trpc.admin.instrument.createSystemUser.useMutation();
  const regenerateApiKey = trpc.admin.instrument.regenerateApiKey.useMutation();
  const removeSystemUser = trpc.admin.instrument.removeSystemUser.useMutation();

  const { control, handleSubmit, reset } = useForm<CreateSystemUserFormValues>({
    defaultValues: {
      username: "",
    },
    resolver: zodResolver(createSystemUserSchema),
  });

  const onSubmitCreate: SubmitHandler<CreateSystemUserFormValues> = (
    values,
  ) => {
    createSystemUser.mutate(
      {
        instrumentId: instrument.id,
        ...values,
      },
      mutationResponse({
        errorTitle: "Error creating system user",
        successMessage: "System user created successfully",
        onSuccess() {
          setIsCreateDialogOpen(false);
          reset();
          refetchSystemUsers();
        },
      }),
    );
  };

  const handleRegenerateApiKey = (userId: number) => {
    regenerateApiKey.mutate(
      { instrumentId: instrument.id, userId },
      mutationResponse({
        errorTitle: "Error regenerating API key",
        successMessage: "API key regenerated successfully",
        onSuccess() {
          refetchSystemUsers();
        },
      }),
    );
  };

  const handleRemoveUser = (userId: number) => {
    removeSystemUser.mutate(
      { instrumentId: instrument.id, userId },
      mutationResponse({
        errorTitle: "Error removing system user",
        successMessage: "System user removed successfully",
        onSuccess() {
          refetchSystemUsers();
        },
      }),
    );
  };

  const handleCopyApiKey = (apiKey: string | null | undefined) => {
    if (apiKey) {
      navigator.clipboard.writeText(apiKey);
      toast.success("API key copied to clipboard");
    }
  };

  const users = (systemUsersData?.users ?? []).map((item) => ({
    ...item,
    id: item.user.id,
  }));

  if (isLoadingSystemUsers) {
    return (
      <TabLayout>
        <Text>Loading system users...</Text>
      </TabLayout>
    );
  }

  return (
    <TabLayout
      title="System Users"
      description={`Manage system users for this instrument to enable API authentication. ${users.length} user${users.length !== 1 ? "s" : ""} configured.`}
      actions={
        <DialogTrigger
          isOpen={isCreateDialogOpen}
          onOpenChange={setIsCreateDialogOpen}
        >
          <Button>
            <PlusIcon className="size-4" />
            Add System User
          </Button>
          <Modal>
            <Dialog>
              <DialogHeader>
                <DialogTitle>Create System User</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit(onSubmitCreate)}>
                <DialogBody>
                  <div className="space-y-4">
                    <Controller
                      control={control}
                      name="username"
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          value={field.value ?? ""}
                          isInvalid={!!error}
                          validationBehavior="aria"
                        >
                          <Label>Username</Label>
                          <Input placeholder="panther-01-system" />
                          <Description>
                            Username for the system user. Should be descriptive
                            and unique.
                          </Description>
                          <FieldError>{error?.message}</FieldError>
                        </TextField>
                      )}
                    />
                  </div>
                </DialogBody>
                <DialogFooter>
                  <Button type="submit" isPending={createSystemUser.isPending}>
                    Create User
                  </Button>
                  <DialogCloseButton variant="outline">
                    Cancel
                  </DialogCloseButton>
                </DialogFooter>
              </form>
            </Dialog>
          </Modal>
        </DialogTrigger>
      }
    >
      {users.length === 0 ? (
        <EmptyState className="py-12">
          <EmptyStateHeading>No system users</EmptyStateHeading>
          <EmptyStateDescription>
            No system users have been created for this instrument yet. Create a
            system user to enable API authentication for instrument interfacing.
          </EmptyStateDescription>
        </EmptyState>
      ) : (
        <div className="rounded-lg border overflow-hidden max-w-4xl">
          <TableRAC aria-label="System users">
            <TableRAC.Header>
              <TableRAC.Row>
                <TableRAC.Column isRowHeader width={180}>
                  Username
                </TableRAC.Column>
                <TableRAC.Column width={100}>Status</TableRAC.Column>
                <TableRAC.Column width="1fr">API Key</TableRAC.Column>
                <TableRAC.Column width={100}>Expires</TableRAC.Column>
                <TableRAC.Column width={80}>Actions</TableRAC.Column>
              </TableRAC.Row>
            </TableRAC.Header>
            <TableRAC.Body items={users}>
              {({ user, hasValidApiKey, apiKey, expiresAt }) => (
                <TableRAC.Row id={user.id}>
                  <TableRAC.Cell>
                    <span className="font-medium">{user.username}</span>
                  </TableRAC.Cell>
                  <TableRAC.Cell>
                    {hasValidApiKey ? (
                      <Badge color="green">Active</Badge>
                    ) : (
                      <Badge color="red">Inactive</Badge>
                    )}
                  </TableRAC.Cell>
                  <TableRAC.Cell>
                    {apiKey ? (
                      <div className="flex items-center gap-1.5">
                        <code className="text-xs bg-muted px-2 py-1 rounded font-mono whitespace-nowrap">
                          {revealedKeys.has(user.id)
                            ? apiKey
                            : "••••••••••••••••••••••••••••••••"}
                        </code>
                        <TooltipTrigger>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onPress={() => toggleKeyVisibility(user.id)}
                          >
                            {revealedKeys.has(user.id) ? (
                              <EyeOffIcon className="size-3.5" />
                            ) : (
                              <EyeIcon className="size-3.5" />
                            )}
                          </Button>
                          <Tooltip>
                            {revealedKeys.has(user.id) ? "Hide" : "Reveal"}
                          </Tooltip>
                        </TooltipTrigger>
                        <TooltipTrigger>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onPress={() => handleCopyApiKey(apiKey)}
                          >
                            <CopyIcon className="size-3.5" />
                          </Button>
                          <Tooltip>Copy</Tooltip>
                        </TooltipTrigger>
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">—</span>
                    )}
                  </TableRAC.Cell>
                  <TableRAC.Cell>
                    <span className="text-muted-foreground">
                      {expiresAt
                        ? new Date(expiresAt).toLocaleDateString()
                        : "Never"}
                    </span>
                  </TableRAC.Cell>
                  <TableRAC.Cell>
                    <div className="flex items-center justify-end gap-1">
                      <ConfirmPrompt
                        title="Regenerate API Key"
                        message={`Are you sure you want to regenerate the API key for "${user.username}"? The current key will be invalidated immediately.`}
                        onConfirm={() => handleRegenerateApiKey(user.id)}
                        loading={regenerateApiKey.isPending}
                      >
                        <TooltipTrigger>
                          <Button variant="ghost" size="icon-sm">
                            <RefreshCwIcon className="size-3.5" />
                          </Button>
                          <Tooltip>Regenerate API key</Tooltip>
                        </TooltipTrigger>
                      </ConfirmPrompt>
                      <ConfirmPrompt
                        title="Remove System User"
                        message={`Are you sure you want to remove "${user.username}"? This will deactivate the user and expire all API keys.`}
                        onConfirm={() => handleRemoveUser(user.id)}
                        loading={removeSystemUser.isPending}
                        danger
                      >
                        <TooltipTrigger>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2Icon className="size-3.5" />
                          </Button>
                          <Tooltip>Remove user</Tooltip>
                        </TooltipTrigger>
                      </ConfirmPrompt>
                    </div>
                  </TableRAC.Cell>
                </TableRAC.Row>
              )}
            </TableRAC.Body>
          </TableRAC>
        </div>
      )}
    </TabLayout>
  );
}
